'use client';

import { Descope } from '@descope/nextjs-sdk';
import { LoadingSpinner } from '@/components/ui/spinner';
import { analytics } from '@/app/analytics';
import type { ReadonlyURLSearchParams } from 'next/navigation';

export type LoginMode = 'trial' | 'login' | 'signup';

interface AuthFormContentProps {
    mode: 'login' | 'signup';
    searchParams: ReadonlyURLSearchParams;
    redirectAfterSuccess: string;
    redirectUrl?: string;
    isLoading: boolean;
    onLoadingChange: (loading: boolean) => void;
    onRedirecting: () => void;
    onSuccess: () => void;
    onModeChange: (mode: LoginMode) => void;
}

export function AuthFormContent({
    mode,
    searchParams,
    redirectAfterSuccess,
    redirectUrl,
    isLoading,
    onLoadingChange,
    onRedirecting,
    onSuccess,
    onModeChange,
}: AuthFormContentProps) {
    return (
        <>
            {mode === 'signup' && <p className="font-semibold text-3xl text-gray-900">Create your free account </p>}
            {mode === 'login' && <p className="font-semibold text-3xl text-gray-900">Welcome back!</p>}
            {mode === 'signup' && <p className="text-gray-600 text-base font-inter font-base font-normal"><span className='hidden md:block'>Create your free account to get buyer insights.<br /></span>Full access. No credit card required.</p>}
            {mode === 'login' && <p className="text-gray-600 text-base font-inter font-base font-normal">Access successful deal insights to boost your sales strategies.</p>}
            <div className="flex justify-center items-center pointer-cursor mb-3 pt-[48px] md:pt-[32px] w-full [&>*]:w-full">
                {isLoading && <LoadingSpinner />}
                <div className={isLoading ? 'hidden' : ''}>
                    <Descope
                        flowId={mode === 'signup' ? 'sign-up-new' : 'sign-in-new'}
                        redirectAfterSuccess={redirectAfterSuccess}
                        redirectUrl={redirectUrl}
                        form={{
                            utmMedium: searchParams ? searchParams.get('utm_medium') || '' : '',
                            utmSource: searchParams ? searchParams.get('utm_source') || '' : '',
                            couponCode: searchParams ? searchParams.get('coupon_code') || '' : '',
                            referralCode: searchParams ? searchParams.get('referralCode') || '' : '',
                            currentUserEmail: searchParams ? searchParams.get('email') || '' : '',
                            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                            givenName: searchParams ? searchParams.get('firstName') || '' : '',
                            familyName: searchParams ? searchParams.get('lastName') || '' : '',
                        }}
                        onReady={() => {
                            console.log('User is ready to login');
                            onLoadingChange(false);
                            onSuccess();
                        }}
                        onSuccess={() => {
                            console.log('User is logged in');
                            onRedirecting();
                            if (typeof window !== "undefined") {
                                (window as any).dataLayer = (window as any).dataLayer || [];
                                (window as any).dataLayer.push({
                                    event: mode === 'signup' ? 'signup' : 'login',
                                    method: 'Descope',
                                    utm_medium: searchParams ? searchParams.get('utm_medium') || '' : '',
                                    utm_source: searchParams ? searchParams.get('utm_source') || '' : '',
                                    coupon_code: searchParams ? searchParams.get('coupon_code') || '' : '',
                                });
                            }
                            try {
                                if (mode === 'signup') {
                                    analytics.getTrackerInstance().userSignup();
                                } else {
                                    analytics.getTrackerInstance().userLogin();
                                }
                            } catch (error) {
                                console.warn('Failed to track auth success:', error);
                            }
                        }}
                        onError={(error: unknown) => {
                            console.error('Failed to login', error);
                        }}
                    />
                </div>
            </div>
            {mode === 'signup' && (
                <div className="text-gray-600 text-center text-xs">
                    <p>By continuing, you agree to our
                        <a href="https://www.clearitty.com/legal/terms" target="_blank" rel="noopener noreferrer" className='underline font-semibold ml-1'>Terms & Conditions</a> and <a href="https://www.clearitty.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className='underline font-semibold'>Privacy Policy</a>.
                    </p>
                </div>
            )}
            {mode === 'signup' && (
                <div className="flex justify-center items-center gap-1 my-8 md:mt-[40px]">
                    <p className="text-gray-600 text-sm">Already have an account?</p>
                    <button className="font-inter text-sm text-main hover:text-main" onClick={() => onModeChange('login')}>Login</button>
                </div>
            )}
            {mode === 'login' && (
                <div className="flex justify-center items-center gap-1 my-8 md:mt-[40px]">
                    <p className="text-gray-600 text-sm">Are you new here?</p>
                    <button className="font-inter text-sm text-main hover:text-main" onClick={() => onModeChange('trial')}>Sign up</button>
                </div>
            )}
        </>
    );
}
